import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Card, CardActions, CardContent, Typography } from '@mui/material';

import { formatLumberGrade, formatLumberSpecies, LumberSet } from '@paragon/contract-library';
import { hasMaterialsEditPermission } from '@paragon/login-tools';

import { deleteLumberSet } from '../../../actions/lumberSetActions';
import { makeLumberTreatmentTypeHumanReadable, stockLengthsToString } from '../../../util';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ExpandableDimension } from '../controls/dimensions/ExpandableDimension';
import { cardSx, pushRightSx } from './common/card-styles';
import { CardAction } from './common/CardAction';

export default function LumberSetCard(props: { parentGuid: string; value: LumberSet }) {
  const dispatch = useAppDispatch();
  const hasEditPermission = useAppSelector(hasMaterialsEditPermission);

  const orderedSpecies = props.value.species
    .map((species) => formatLumberSpecies(species, []))
    .concat(props.value.comboSpecies.map((tangibleSpeciesList) => formatLumberSpecies('Combo', tangibleSpeciesList)))
    .toSorted();
  const orderedGrades = props.value.grades
    .map((grade) => formatLumberGrade(grade, []))
    .concat(props.value.comboGrades.map((tangibleGradeList) => formatLumberGrade('Combo', tangibleGradeList)))
    .toSorted();
  const orderedTreatments = props.value.treatmentTypes.map(makeLumberTreatmentTypeHumanReadable).toSorted();

  return (
    <Card elevation={0} sx={cardSx}>
      <CardContent>
        <Typography variant="h5">{orderedSpecies.join(', ')}</Typography>

        <Typography variant="body1" color="textSecondary">
          Grades: {orderedGrades.join(', ')}
        </Typography>

        <Typography variant="body1" color="textSecondary">
          Treatments: {orderedTreatments.join(', ')}
        </Typography>

        <Typography variant="body1" color="textSecondary">
          Dimensions:{' '}
          <ExpandableDimension first={props.value.actualDimensions1} second={props.value.actualDimensions2} />
        </Typography>

        <Typography variant="body1" color="textSecondary" noWrap>
          Stock Lengths: {stockLengthsToString(props.value.stockLengths)}
        </Typography>
      </CardContent>
      <CardActions disableSpacing>
        <Box sx={pushRightSx} />
        {hasEditPermission && (
          <>
            <CardAction tooltip="Edit" linkTo={`/lumberLists/${props.parentGuid}/lumberSets/${props.value.guid}/edit`}>
              <EditIcon />
            </CardAction>
            <CardAction
              tooltip="Delete"
              onClick={() => {
                dispatch(deleteLumberSet(props.parentGuid, props.value.guid));
              }}
            >
              <DeleteIcon />
            </CardAction>
          </>
        )}
      </CardActions>
    </Card>
  );
}
